import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  static #cachedContent = new Map();
  static #fetchAbortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  #resetScroll() {
    const productDetails = this.querySelector('.product-details');
    const productMedia = this.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /**
   * Opens the modal to display the given product details
   * @param {string} productPageUrl - The product URL
   * @param {string} [productTitle] - The product title
   * @param {string | null} [selectedVariantId] - Optional variant ID
   */
  /**
   * Opens the modal to display the given product details
   * @param {string} productPageUrl - The product URL
   * @param {string} [productTitle] - The product title
   * @param {string | null} [selectedVariantId] - Optional variant ID
   * @param {string} [fallbackImg] - Optional fallback image URL from product card
   */
  async openForProduct(productPageUrl, productTitle = '', selectedVariantId = null, fallbackImg = '') {
    if (!productPageUrl) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    // Immediately show responsive preview skeleton with fallback image if available
    let initialImg = fallbackImg || '';
    if (initialImg) {
      initialImg = initialImg.replace(/width=\d+/, 'width=800');
    }

    modalContent.innerHTML = `
      <div class="product-information__media">
        <div class="quick-view-media-box">
          ${initialImg ? `<img src="${initialImg}" alt="${productTitle}" class="quick-view-hero-image" />` : '<div class="quick-view-spinner"></div>'}
        </div>
      </div>
      <div class="product-details">
        <div class="pdp-header-block">
          <h1 class="pdp-title">${productTitle}</h1>
        </div>
        <div class="quick-view-loading-text">
          <div class="quick-view-spinner-sm"></div>
          <span>Loading product options & details...</span>
        </div>
      </div>
    `;

    this.showDialog();
    const closeBtn = this.querySelector('.quick-add-modal__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });

    let productGrid = QuickAddDialog.#cachedContent.get(productPageUrl);

    if (!productGrid) {
      QuickAddDialog.#fetchAbortController?.abort();
      QuickAddDialog.#fetchAbortController = new AbortController();

      try {
        const response = await fetch(productPageUrl, {
          signal: QuickAddDialog.#fetchAbortController.signal,
        });

        if (!response.ok) {
          throw new Error(`Failed to load product preview: HTTP ${response.status}`);
        }

        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          QuickAddDialog.#cachedContent.set(productPageUrl, productGrid);
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Quick view error:', error);
        modalContent.innerHTML = `
          <div class="quick-view-error">
            <p>Could not load product preview.</p>
            <a href="${productPageUrl}" class="button button--secondary">View Product Page</a>
          </div>
        `;
        return;
      } finally {
        QuickAddDialog.#fetchAbortController = null;
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));

      // Remove PDP-only elements, accordions, bulk quote modals, duplicate titles, and accelerated checkout (Buy it now)
      freshContent.querySelectorAll(
        '.pdp-bulk-quote-container, .pdp-bulk-quote-prompt, .pdp-modal-backdrop, #PDPBulkQuoteModal, #PDPBulkQuoteTrigger, pdp-structured-content, .pdp-structured-content, .pdp-subtitle, .product-accordions, .accordion, .divider, .pdp-divider, .breadcrumbs, .view-product-title, .pdp-sticky-bar, .accelerated-checkout-block, .shopify-payment-button, [data-shopify="payment-button"], shopify-accelerated-checkout-cart'
      ).forEach(el => el.remove());

      // Prepare left column with an optimized hero image (max 800px width for fast loading)
      const foundImg = freshContent.querySelector('.product-information__media img, .product-media img, img');
      let imageSrc = foundImg?.getAttribute('src') || foundImg?.src || initialImg || '';
      if (imageSrc) {
        if (imageSrc.includes('width=')) {
          imageSrc = imageSrc.replace(/width=\d+/, 'width=800');
        } else {
          imageSrc += (imageSrc.includes('?') ? '&' : '?') + 'width=800';
        }
      }

      const mediaCol = freshContent.querySelector('.product-information__media');
      if (mediaCol) {
        mediaCol.innerHTML = `
          <div class="quick-view-media-box">
            <img src="${imageSrc}" alt="${productTitle}" class="quick-view-hero-image" loading="eager" decoding="async" />
          </div>
        `;
      }

      // Format right column details
      const details = freshContent.querySelector('.product-details');
      if (details) {
        // Ensure clean single title
        details.querySelectorAll('.view-product-title').forEach(el => el.remove());
        let headerBlock = details.querySelector('.pdp-header-block');
        if (headerBlock) {
          let titleEl = headerBlock.querySelector('.pdp-title');
          if (!titleEl) {
            titleEl = document.createElement('h1');
            titleEl.className = 'pdp-title';
            titleEl.textContent = productTitle;
            headerBlock.prepend(titleEl);
          }
        } else if (!details.querySelector('.pdp-title')) {
          const titleEl = document.createElement('h1');
          titleEl.className = 'pdp-title';
          titleEl.textContent = productTitle;
          details.prepend(titleEl);
        }

        // Place clean "View full product specifications →" link directly under the add to cart button
        if (!details.querySelector('.quick-view-full-details-link')) {
          const fullLinkWrap = document.createElement('div');
          fullLinkWrap.className = 'quick-view-full-details-wrap';
          fullLinkWrap.innerHTML = `<a href="${productPageUrl}" class="quick-view-full-details-link">View full product specifications &rarr;</a>`;
          
          const actionRow = details.querySelector('.pdp-action-buttons-row');
          const buyButtonsBlock = details.querySelector('.buy-buttons-block') || details.querySelector('product-form-component') || details.querySelector('.product-form-buttons');
          
          if (actionRow) {
            actionRow.insertAdjacentElement('afterend', fullLinkWrap);
          } else if (buyButtonsBlock) {
            buyButtonsBlock.insertAdjacentElement('afterend', fullLinkWrap);
          } else {
            details.appendChild(fullLinkWrap);
          }
        }
      }

      // Mount into modal cleanly
      modalContent.replaceChildren(...freshContent.childNodes);

      // Ensure "View full product specifications" is directly under the add to cart buttons
      const mountedActionRow = modalContent.querySelector('.pdp-action-buttons-row');
      const mountedLink = modalContent.querySelector('.quick-view-full-details-wrap');
      if (mountedActionRow && mountedLink) {
        mountedActionRow.insertAdjacentElement('afterend', mountedLink);
      }

      // Wire interactive color swatches in Quick View
      modalContent.querySelectorAll('.pdp-color-swatch-circle').forEach(swatch => {
        swatch.addEventListener('click', (e) => {
          e.preventDefault();
          const row = swatch.closest('.pdp-color-swatches-row');
          if (row) {
            row.querySelectorAll('.pdp-color-swatch-circle').forEach(s => {
              s.classList.remove('is-selected');
              s.setAttribute('aria-checked', 'false');
            });
          }
          swatch.classList.add('is-selected');
          swatch.setAttribute('aria-checked', 'true');
          const group = swatch.closest('.pdp-variant-group');
          const chosen = group?.querySelector('.pdp-variant-chosen');
          if (chosen && swatch.dataset.color) {
            chosen.textContent = swatch.dataset.color;
          }
        });
      });

      // Wire interactive pill option buttons in Quick View
      modalContent.querySelectorAll('.pdp-pill-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          if (btn.classList.contains('is-unavailable') || btn.hasAttribute('disabled')) return;
          const row = btn.closest('.pdp-pill-options-row');
          if (row) {
            row.querySelectorAll('.pdp-pill-btn').forEach(b => {
              b.classList.remove('is-active');
              b.setAttribute('aria-checked', 'false');
            });
          }
          btn.classList.add('is-active');
          btn.setAttribute('aria-checked', 'true');
          const group = btn.closest('.pdp-variant-group');
          const chosen = group?.querySelector('.pdp-variant-chosen');
          if (chosen && btn.dataset.value) {
            chosen.textContent = btn.dataset.value;
          }
        });
      });

      // Handle standard variant picker if present
      const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
      if (variantPicker) {
        variantPicker.updateVariantPicker?.(productGrid);
      }

      if (selectedVariantId) {
        const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
        for (const input of modalInputs) {
          if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
            input.checked = true;
            break;
          }
        }
      }

      // Reset scroll cleanly and maintain focus on close button without auto-scrolling
      requestAnimationFrame(() => {
        if (closeBtn) closeBtn.focus({ preventScroll: true });
        const detailsEl = modalContent.querySelector('.product-details');
        if (detailsEl) detailsEl.scrollTop = 0;
        const mediaEl = modalContent.querySelector('.product-information__media');
        if (mediaEl) mediaEl.scrollTop = 0;
        modalContent.scrollTop = 0;
      });
    }
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    QuickAddDialog.#cachedContent.clear();
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}

// Global delegated click listener for Quick View buttons
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-quick-view-btn]');
  if (!btn) return;

  event.preventDefault();
  event.stopPropagation();

  btn.classList.add('is-loading');

  const productCard = btn.closest('product-card, .col-product-card');
  const cardLink = productCard?.querySelector('a[ref="productCardLink"], .product-card__link, .col-product-card__image-link');
  const productUrl = btn.dataset.productUrl || cardLink?.href;
  const productTitle =
    btn.dataset.productTitle ||
    productCard?.querySelector('.product-title, .col-product-card__title a')?.textContent?.trim() ||
    '';
  const variantId = btn.dataset.variantId || productCard?.dataset?.productId || null;
  const cardImg = productCard?.querySelector('img')?.src || '';

  const dialog = document.getElementById('quick-add-dialog');
  if (dialog instanceof QuickAddDialog) {
    dialog.openForProduct(productUrl, productTitle, variantId, cardImg).finally(() => {
      btn.classList.remove('is-loading');
    });
  } else {
    btn.classList.remove('is-loading');
    if (productUrl) {
      window.location.href = productUrl;
    }
  }
});
